import type { KeyboardLayout } from "@/domain/types";
import { convert } from "@/games/sc2/parser/convert";
import type { CatalogEntry } from "@/platform/thecore/ingest";

/** The .SC2Hotkeys file to download for a Preset under one Keyboard Layout. */
export interface PresetDownloadFile {
  fileName: string;
  /** A static asset path when the layout is vendored verbatim; absent when generated. */
  href?: string;
  /** Generated file text when the layout is not vendored; absent when served from href. */
  text?: string;
}

/**
 * The .SC2Hotkeys text a Preset serves under one Keyboard Layout: the vendored canonical seed
 * verbatim, or the conversion-engine output for every other layout. The ONE text both the
 * download and the rendered board consume (decision 0022 amendment: the picture depicts the
 * file the Download button hands out, so the two agree by construction).
 */
export function servedText(entry: CatalogEntry, layout: KeyboardLayout): string {
  const vendored = entry.downloads.find((d) => d.layoutId === layout.id);
  return vendored ? entry.text : convert(entry.text, layout);
}

/**
 * The text one render side ingests (the 0022 amendment's dispatch): a published Preset renders
 * the file its Download serves; everything else (an upload, a stock table, a pinned source) has
 * no generated download - its own text IS the artifact - and renders character-space.
 */
export function renderText(
  preset: CatalogEntry | null,
  sourceText: string,
  layout: KeyboardLayout | null,
): string {
  return preset && layout ? servedText(preset, layout) : sourceText;
}

/**
 * Resolve the download for a Preset in the active Keyboard Layout. The
 * canonical US-Qwerty serialization is vendored and served byte-faithfully (its static asset);
 * every other Keyboard Layout is generated from the seed by the conversion engine -
 * derive-don't-store, no backend. The generated file keeps the vendored
 * name with its layout tag swapped, so a downloaded AZERTY file reads ..._right_AZERTY.SC2Hotkeys.
 */
export function presetDownload(entry: CatalogEntry, layout: KeyboardLayout): PresetDownloadFile {
  const vendored = entry.downloads.find((d) => d.layoutId === layout.id);
  if (vendored) return { fileName: vendored.fileName, href: vendored.path };
  const base = entry.downloads[0]?.fileName ?? `${entry.id}.SC2Hotkeys`;
  const fileName = base.replace(/US[_ ]?Qwerty/i, layout.name.replace(/\s+/g, "_"));
  return { fileName, text: servedText(entry, layout) };
}
